/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';

import { Subject, map } from 'rxjs';

import { MatDialogRef } from '@angular/material/dialog';
import { CdsPopupService } from '@cds/ng-web-components/popup';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';

import { DateTime } from 'luxon';
import { BTN_CONFIG_PRIMARY, BTN_CONFIG_SECONDARY } from '../../../config/btn.config';
import { MCF_REDIRECT_STATES } from '../../../config/mcf-redirect.config';
import { Session } from '../../../core/models/user';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { OtpService } from '../../../core/services/otp/otp.service';
import { ToastAlertService } from '../../../shared/toast-alert.service';
import { addSessionByKey, deleteDB } from '../../../utils/indexdb';
import { OTPChannel } from './otp-channel.enum';
import { environment } from 'src/environments/environment';
import { CdsLangService, CdsLanguageModule } from '@cds/ng-core/lang';
import { UserAgreementPopupComponent } from 'src/app/shared/user-agreement-popup/user-agreement-popup.component';
import { IffService } from '../../salesjourney/service/iff.service';
import { SessionConflictService } from '../../../core/services/session-conflict.service';
import { LanguageChangeService } from '../../../shared/language-change.service';
import { CdsButtonModule } from '@cds/ng-web-components/button';
import { FormsModule } from '@angular/forms';
import { CdsIconModule } from '@cds/ng-web-components/icon';
import { CdsAssistiveTextModule } from '@cds/ng-web-components/assistive-text';
import { CdsProgressModule } from '@cds/ng-web-components/progress';
import { MyStringPipe } from './my-string.pipe';
import { InternalLoginUrlService } from '../../../core/services/internal-login-url.service';
import { ReleaseNotificationCardComponent } from '../../../shared/release-notification/release-notification-card.component';
import { ReleaseNotificationService } from '../../../shared/release-notification/release-notification.service';
import { getMcfRedirectUrl } from './mcf-redirect-url';

export type LoginType = 'otp' | 'sso' | 'mcf';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    CdsButtonModule,
    FormsModule,
    CdsIconModule,
    CdsAssistiveTextModule,
    CdsProgressModule,
    CdsLanguageModule,
    MyStringPipe,
    AsyncPipe,
    ReleaseNotificationCardComponent,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  private authService = inject(MsalService);
  private cdsPopup = inject(CdsPopupService);
  private authenticationService = inject(AuthenticationService);
  private router = inject(Router);
  private otpService = inject(OtpService);
  private toastAlert = inject(ToastAlertService);
  private langService = inject(CdsLangService);
  private iffService = inject(IffService);
  private sessionConflictService = inject(SessionConflictService);
  private languageChangeService = inject(LanguageChangeService);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private internalLoginUrlService = inject(InternalLoginUrlService);
  private releaseNotificationService = inject(ReleaseNotificationService);

  loginBtnCfg = BTN_CONFIG_PRIMARY;
  btnCfg = BTN_CONFIG_SECONDARY;

  //otp login
  isInternal = false;
  isExternal = false;
  isLoginEnable = false;
  isShowOTP = false;
  isShowLoading = false;
  isContinueEnable = false;
  isResendEnable = false;
  isPageReady = false;
  masked = true;

  loginAnnouncements$ = this.releaseNotificationService.getAnnouncements().pipe(map(announcements => announcements.slice(0, 2)));

  inputType = 'password';
  second = '60';
  otp = '';
  otpErr = '';
  OTPUser: any = {};
  loginForm: any = {
    username: '',
    passwd: '',
    loginErr: '',
  };

  profile = environment.profile;
  mCFMode = environment.mCFMode;
  pwdLogin = environment.pwdLogin;
  mCFAPISuffix = '/auth/mcf/token';

  private readonly _destroying$ = new Subject<void>();
  private SSO_API_SUFFIX = '/auth/ms/login';
  private OTP_LOGIN_API_SUFFIX = '/auth/otp/token';
  private DIALOG_DELAY = 5000;

  async ngOnInit(): Promise<void> {
    if (this.internalLoginUrlService.isBlockedHost(window.location.hostname)) {
      this.router.navigate(['/error/deprecated-url']);
      return;
    }

    let data = new Session();
    try {
      data = await this.authenticationService.currentUserValue();
    } catch (error) {
      console.error('currentUserValue failed', error);
    }

    this.ngZone.run(() => {
      const obj = this.loginWithMCF();
      if (obj) {
        const popupRef = this.authenticationService.getLoadingDialogRef();
        this.authenticateToken('mcf', obj.accessToken, popupRef, this.mCFAPISuffix, obj.mcfRefreshToken);
        return;
      }

      if (data.token !== undefined) {
        this.router.navigate(['/home']);
        return;
      }

      const afterUrl: string = window.location.href;

      console.log('afterUrl:' + afterUrl);
      this.isInternal = this.internalLoginUrlService.isInternalUrl(afterUrl);
      this.isPageReady = true;

      // Render only after internal/external mode has been finalized.
      this.cdr.detectChanges();

      console.log('this.isInternal:' + this.isInternal);
    });
  }

  redirectMCF() {
    const url = environment.mcfUrl + '&lang=' + (this.langService.currentLang && this.langService.currentLang == 'zh' ? 'zh-hant' : 'en');
    window.location.href = url;
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  loginFormChange() {
    this.loginForm.loginErr = '';
    this.isLoginEnable = Boolean(this.loginForm.username && this.loginForm.passwd);
  }

  loginWithSSO() {
    const popupRef = this.authenticationService.getLoadingDialogRef();
    this.authenticationService.loginWithSSO().subscribe({
      next: (result: AuthenticationResult) => {
        this.authService.instance.setActiveAccount(result.account);
        this.authenticateToken('sso', result.accessToken, popupRef, this.SSO_API_SUFFIX);
      },
      error: (error: any) => {
        console.log(error);
        popupRef.close();
      },
    });
  }

  loginWithMCF() {
    const afterUrl: string = window.location.href;
    if (afterUrl != null && afterUrl != '' && afterUrl.indexOf('?') != -1 && afterUrl.indexOf('mcf_token') != -1) {
      let paramsStr = '';
      if (afterUrl.length > afterUrl.indexOf('?')) {
        paramsStr = afterUrl.slice(afterUrl.indexOf('?') + 1, afterUrl.length);
      }
      let params: string[] = [];
      if (afterUrl.indexOf('&') != -1) {
        params = paramsStr.split('&');
      } else {
        params.push(paramsStr);
      }
      if (params && params.length > 0) {
        let accessToken = '';
        let mcfRefreshToken = '';
        let lang = '';
        let state = '';
        let a = 0;
        params.forEach(item => {
          if (item.indexOf('mcf_token') != -1) a = 1;
          if (item.indexOf('mcf_refresh_token') != -1) a = 2;
          if (item.startsWith('lang=')) {
            lang = item.slice(5);
          } else if (item.startsWith('state=')) {
            state = item.slice(6);
          } else if (item && item.indexOf('=') != -1 && a != 0 && ((a == 1 && !accessToken) || (a == 2 && !mcfRefreshToken))) {
            let index = 0;
            for (let i = 0; i < item.length; i++) {
              if (item[i] == '=') {
                index = i;
                break;
              }
            }
            if (index > 0 && item.length > index + 1) {
              if (a == 1) accessToken = item.slice(index + 1, item.length);
              if (a == 2) mcfRefreshToken = item.slice(index + 1, item.length);
            } else {
              window.alert('mcf token pattern error');
            }
          }
        });
        if (new URL(document.baseURI).pathname === '/' && MCF_REDIRECT_STATES.includes(state) && this.redirectToStateHref(state)) {
          return false;
        }
        if (accessToken && mcfRefreshToken) {
          if (lang === 'zh-hant') {
            this.languageChangeService.setLang('zh');
          } else if (lang === 'en') {
            this.languageChangeService.setLang('en');
          }
          return {
            accessToken,
            mcfRefreshToken,
            state,
          };
        } else {
          window.alert('mcf token url error');
        }
      }
    }
    return false;
  }

  private redirectToStateHref(state: string): boolean {
    const redirectUrl = getMcfRedirectUrl(window.location.href, state);
    if (!redirectUrl) {
      return false;
    }

    window.location.href = redirectUrl;
    return true;
  }

  loginWithManutouch() {
    if (!this.isLoginEnable) return;
    this.loginForm.loginErr = '';
    const popupRef = this.authenticationService.getLoadingDialogRef();
    const username = this.loginForm.username;
    const password = this.loginForm.passwd;
    this.otpService.login({ userId: username, password: password }).subscribe({
      next: res => {
        if (res.result !== 0) {
          popupRef.close();
          this.isShowOTP = false;
          this.setLoginErrorWithCd(res.message);
        } else {
          this.OTPUser = {
            agentKey: res.data.agentKey,
            emailAddress: res.data.emailAddress,
            phoneNum: res.data.phoneNum,
            sessionToken: res.data.sessionToken,
          };
          this.openOtpStep(popupRef);
        }
      },
      error: err => {
        this.handleError(err.message, 'manutouch', popupRef);
      },
    });
  }

  loginWithOTP() {
    this.otpErr = '';
    const popupRef = this.authenticationService.getLoadingDialogRef();
    this.otpService.verifyOTP({ sessionToken: this.OTPUser.sessionToken, otp: this.otp }).subscribe({
      next: res => {
        if (res.result !== 0) {
          popupRef.close();
          this.setOtpErrorWithCd(res.message);
        } else {
          const accessToken = res.data.accessToken;
          this.authenticateToken('otp', accessToken, popupRef, this.OTP_LOGIN_API_SUFFIX);
        }
      },
      error: err => {
        this.handleError(err.message, 'otp', popupRef);
      },
    });
  }

  requestOTP(popupRef?: any, pageResend?: boolean) {
    if (pageResend && this.second !== '0-1' && !this.isResendEnable) return;
    this.otp = '';
    this.otpErr = '';
    this.isShowLoading = true;
    this.otpService
      .requestOTP({
        sessionToken: this.OTPUser.sessionToken,
        channel: OTPChannel.E,
      })
      .subscribe({
        next: res => {
          if (res.result !== 0) {
            popupRef?.close();
            this.applyOtpUiState(false, true, res.message);
          } else {
            popupRef?.close();
            this.countDown();
            this.OTPUser.sessionToken = res.data.sessionToken;
            this.applyOtpUiState(false, false);
          }
        },
        error: err => {
          this.applyOtpUiState(false, true);
          this.handleError(err.message, 'otp', popupRef);
        },
      });
  }

  changeOTP(value: number) {
    this.otpErr = '';
    this.isContinueEnable = !!value && String(value).length === 6;
  }

  backToLogin() {
    this.OTPUser = {};
    this.otp = '';
    this.otpErr = '';
    this.isContinueEnable = false;
    this.isShowOTP = false;
  }

  toggleMask() {
    this.masked = !this.masked;
    this.inputType = this.masked ? 'password' : 'text';
  }

  private countDown() {
    // every one minute can resend OTP (unit: seconds)
    let clock = 60;
    const myInterval = setInterval(() => {
      if (clock == -1) {
        clearInterval(myInterval);
      }
      this.second = String(clock < 10 ? '0' + clock : clock);
      clock--;
    }, 1000);
  }

  private authenticateToken(
    loginType: LoginType,
    accessToken: string,
    popupRef: MatDialogRef<UserAgreementPopupComponent>,
    apiSuffix: string,
    mcfRefreshToken?: string
  ) {
    this.authenticationService.authenticateToken(apiSuffix, accessToken, mcfRefreshToken).subscribe({
      next: async res => {
        const end = DateTime.now().toMillis();
        console.log('login backend response time:' + end);
        popupRef.close();
        if (res.result === 0) {
          const userInfo: Session = res.data;
          if (userInfo) {
            const targetUrl = sessionStorage.getItem('targetUrl') || '/home';
            const i = targetUrl.indexOf('?');
            const queryParams: { [key: string]: string } = {};
            if (i > -1) {
              const searchParams = new URLSearchParams(targetUrl.substring(i));
              searchParams.forEach((value, key) => {
                queryParams[key] = value;
              });
            }
            const mParams = this.parseMatrixParams(targetUrl);

            userInfo.currentLastUpdate = DateTime.now().toMillis();

            const hasConflict = await this.sessionConflictService.hasConflict(userInfo.id);
            if (hasConflict) {
              await this.sessionConflictService.showConflictDialog();
            }

            await addSessionByKey(userInfo).then(() => {
              let naviUrl = targetUrl;
              if (i > -1) {
                naviUrl = targetUrl.substring(0, i);
              } else if (targetUrl.includes(';')) {
                naviUrl = targetUrl.substring(0, targetUrl.indexOf(';'));
              }
              this.sessionConflictService.broadcastSessionChange(userInfo.id);
              this.iffService.getServicingAgentCodeAndChannel();
              this.router.navigate([naviUrl, mParams], { queryParams });
            });
          }

          localStorage.setItem('loginType', loginType);
        } else {
          deleteDB();
          let msg = res.message;
          switch (res.result) {
            case 2008:
              msg = 'login.errorMsg.mcf2008';
              break;
            case 2010:
              msg = 'login.errorMsg.mcf2010';
              break;
            case 2019:
              msg = 'login.errorMsg.mcf2019';
              break;
          }
          this.handleError(msg, loginType, popupRef);
        }
      },
      error: err => {
        this.handleError(err.message, loginType, popupRef);
      },
    });
  }

  private handleError(message: string, loginType: string, popupRef?: any) {
    let errMsg = message;
    if (message.indexOf(':') !== -1) {
      errMsg = message.substring(message.lastIndexOf(':') + 2, message.length);
    }

    if (loginType === 'otp' && errMsg.indexOf('401') > -1) {
      setTimeout(() => {
        this.backToLogin();
        this.cdr.detectChanges();

        setTimeout(() => {
          popupRef?.close();
          this.toastAlert.show('error', 'common.error', errMsg, this.DIALOG_DELAY);
        }, 0);
      }, 0);
      return;
    }

    popupRef?.close();
    if (loginType === 'sso') {
      this.router.navigate([this.internalLoginUrlService.getLoginPath()]);
      this.toastAlert.show('error', 'common.error', errMsg, this.DIALOG_DELAY);
    } else if (loginType === 'manutouch') {
      this.setLoginErrorWithCd(errMsg);
    } else if (loginType === 'mcf') {
      //this.toastAlert.show('error', 'common.error', errMsg, this.DIALOG_DELAY);
      this.cdsPopup.open(UserAgreementPopupComponent, {
        size: 'md',
        data: {
          message: message,
          continue: 'common.gotIt',
          type: 'alert',
        },
      });
    } else if (loginType === 'otp') {
      this.setOtpErrorWithCd(errMsg);
    }
  }

  private setOtpErrorWithCd(message: string) {
    setTimeout(() => {
      this.otpErr = message;
      this.cdr.detectChanges();
    }, 0);
  }

  private setLoginErrorWithCd(message: string) {
    setTimeout(() => {
      this.loginForm.loginErr = message;
      this.cdr.detectChanges();
    }, 0);
  }

  private openOtpStep(popupRef?: any) {
    setTimeout(() => {
      this.requestOTP(popupRef);
      this.isShowOTP = true;
      this.cdr.detectChanges();
    }, 0);
  }

  private applyOtpUiState(isShowLoading: boolean, isResendEnable: boolean, otpErr?: string) {
    setTimeout(() => {
      this.isShowLoading = isShowLoading;
      this.isResendEnable = isResendEnable;
      if (otpErr !== undefined) {
        this.otpErr = otpErr;
      }
      this.cdr.detectChanges();
    }, 0);
  }

  private parseMatrixParams(url: string) {
    const lastSegment = url.split('/').pop();
    if (!lastSegment || !lastSegment.includes(';')) {
      return {};
    }
    const paramsString = lastSegment.split(';').slice(1).join(';');
    const params: { [key: string]: string } = {};
    if (paramsString) {
      const pairs = paramsString.split(';');
      pairs.forEach(pair => {
        const [key, value] = pair.split('=');
        if (key && value !== undefined) {
          params[key] = value;
        }
      });
    }
    return params;
  }
}
